import { useState } from 'react';
import { Icon, showToast, EmptyState } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  IntegrationCard,
  WebhookCard,
  LoadingState,
  ErrorState,
  OrganizationCard,
  ClioConfigModal,
  QBOConfigModal,
  EmailConfigModal,
  TenantSettingsModal,
} from '../components';
import {
  useTenantConfig,
  useConfigurationStatus,
  useClioConfigMutation,
  useQboConfigMutation,
  useTenantSettingsMutation,
  useEmailConfigMutation,
  useModuleHealthCheck,
} from '../queries';
import type {
  ClioModuleConfig,
  ModuleConfig,
  EmailModuleConfig,
} from '@xtnd/web/shared/types/tenantSettingTypes';

export default function SettingsPage() {

  // Modal states
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [moduleHealthData, setModuleHealthData] = useState<Record<string, any>>({});

  // Fetch tenant configuration
  const {
    data: tenantConfig,
    isLoading: isLoadingConfig,
    error,
    isError,
    refetch,
  } = useTenantConfig();

  // Fetch configuration status (only after tenant config is loaded)
  const {
    data: configurationStatus,
    isLoading: isLoadingStatus,
  } = useConfigurationStatus(!!tenantConfig);

  // Mutations
  const clioConfigMutation = useClioConfigMutation();
  const qboConfigMutation = useQboConfigMutation();
  const tenantSettingsMutation = useTenantSettingsMutation();
  const emailConfigMutation = useEmailConfigMutation();
  const moduleHealthCheckMutation = useModuleHealthCheck();

  // Combined loading state - wait for both APIs
  const isLoading = isLoadingConfig || (!!tenantConfig && isLoadingStatus);

  // Show error toast when tenant config fails
  if (isError && error instanceof Error) {
    showToast.error(`Failed to load settings: ${error.message}`);
  }

  const handleConfigureIntegration = (moduleName: string) => {
    setActiveModal(moduleName);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleHealthCheck = async (moduleName: string) => {
    setModuleHealthData(prev => ({
      ...prev,
      [moduleName]: { status: 'checking', message: 'Checking module health...' },
    }));

    try {
      const result = await moduleHealthCheckMutation.mutateAsync(moduleName);
      setModuleHealthData(prev => ({
        ...prev,
        [moduleName]: result.health[moduleName],
      }));
    } catch {
      setModuleHealthData(prev => ({
        ...prev,
        [moduleName]: {
          status: 'error',
          message: 'Health check failed',
          lastChecked: new Date().toISOString(),
        },
      }));
    }
  };

  // Helper function to filter active integrations
  const getActiveIntegrations = () => {
    if (!tenantConfig?.features) return [];
    return tenantConfig.features.filter(
      f => f.enabled && f.moduleName !== 'emailTemplates' && f.moduleName !== 'pdfTemplates'
    );
  };

  if (isError && error instanceof Error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }

  return (
    <PageLayout
      title="Settings"
      description="Manage integrations, webhooks, and system configuration"
      icon="HiCog"
      isLoading={isLoading}
      customSkeleton={<LoadingState />}
    >
      <div className="space-y-8">

      {/* Organization Info Card */}
      <OrganizationCard
        tenantName={tenantConfig?.tenantName}
        tenantId={tenantConfig?.tenantId}
        onEdit={() => setActiveModal('tenantSettings')}
      />

      {/* Main Content */}
      <div className="space-y-8">
        {/* Integrations Section */}
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <Icon name="HiPuzzle" className="h-6 w-6 text-xtnd-blue" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Active Integrations
            </h2>
          </div>

          {getActiveIntegrations().length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getActiveIntegrations().map(feature => {
                  const configStatus = configurationStatus?.modules.find(
                    module => module.module === feature.moduleName
                  );

                  return (
                    <IntegrationCard
                      key={feature.moduleName}
                      feature={feature}
                      moduleConfig={
                        tenantConfig?.modules[feature.moduleName] || ({} as ModuleConfig)
                      }
                      onConfigure={handleConfigureIntegration}
                      onHealthCheck={handleHealthCheck}
                      health={moduleHealthData[feature.moduleName]}
                      configurationStatus={configStatus}
                    />
                  );
                })}
            </div>
          ) : (
            <EmptyState
              icon="HiPuzzle"
              title="No Integrations Enabled"
              description="Contact your administrator to enable integrations"
              variant="default"
              padding="md"
              titleSize="sm"
            />
          )}
        </div>

        {/* Webhooks Section */}
        {(tenantConfig?.modules?.clioAPI || tenantConfig?.modules?.qboAPI) && (
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <Icon name="HiGlobe" className="h-6 w-6 text-xtnd-blue" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Webhook Management
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Clio Webhooks */}
              {tenantConfig?.modules?.clioAPI && (
                <WebhookCard
                  title="Clio Webhooks"
                  description="Real-time updates for bills, matters, and contacts from Clio"
                  iconColor="blue"
                  isActive={!!tenantConfig.modules.clioAPI.webhooksConfigured}
                  manageRoute="/settings/webhooks"
                  activityRoute="/settings/webhook-activity"
                  features={[
                    { icon: 'HiDocumentText', label: 'Lifecycle Manager' },
                    { icon: 'HiChartBar', label: 'Activity Monitor' },
                  ]}
                />
              )}

              {/* QBO Webhooks */}
              {tenantConfig?.modules?.qboAPI && (
                <WebhookCard
                  title="QuickBooks Online"
                  description="Auto-process invoices, customers, and payments from QBO"
                  iconColor="green"
                  isActive={!!tenantConfig.modules.qboAPI.configured}
                  manageRoute="/settings/qbo-webhooks"
                  activityRoute="/settings/qbo-webhook-activity"
                  features={[
                    { icon: 'HiLightningBolt', label: 'Auto-Processing' },
                    { icon: 'HiCollection', label: 'Queue Management' },
                  ]}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dynamic Configuration Modals */}
      <ClioConfigModal
        isOpen={activeModal === 'clioAPI'}
        onClose={handleCloseModal}
        onSave={async data => {
          await clioConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={tenantConfig?.modules?.clioAPI as ClioModuleConfig | undefined}
        isLoading={clioConfigMutation.isPending}
      />

      <QBOConfigModal
        isOpen={activeModal === 'qboAPI'}
        onClose={handleCloseModal}
        onSave={async data => {
          await qboConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={tenantConfig?.modules?.qboAPI}
        isLoading={qboConfigMutation.isPending}
      />

      <TenantSettingsModal
        isOpen={activeModal === 'tenantSettings'}
        onClose={handleCloseModal}
        onSave={async data => {
          await tenantSettingsMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={
          {
            ...tenantConfig?.modules.tenantSettings,
            tenantName: tenantConfig?.tenantName,
            alias: tenantConfig?.alias,
            authorizedDomains: tenantConfig?.authorizedDomains,
            emailServiceConfig: tenantConfig?.modules?.emailServiceConfig,
            updatedAt: tenantConfig?.updatedAt,
          } as any
        }
        isLoading={tenantSettingsMutation.isPending}
      />

      <EmailConfigModal
        isOpen={activeModal === 'emailNotifications'}
        onClose={handleCloseModal}
        onSave={async data => {
          await emailConfigMutation.mutateAsync(data);
          setActiveModal(null);
        }}
        initialData={
          tenantConfig?.modules.tenantSettings?.emailNotifications as EmailModuleConfig | undefined
        }
        isLoading={emailConfigMutation.isPending}
      />
      </div>
    </PageLayout>
  );
}
